import { authorizedFetch } from './api-client';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export interface BulkReassignRequest {
  clinic_id: string;
  slot_date: string;       // YYYY-MM-DD
  dentist_from_id: string;
  time_from: string;       // HH:MM:SS
  time_to: string;         // HH:MM:SS
  dentist_to_id: string;
  specialty_to_id?: string;
  reason?: string;
}

export interface SlotPreviewItem {
  slot_id: string;
  slot_time: string;
  patient_id: string | null;
  patient_name: string | null;
  has_patient: boolean;
}

export interface BulkReassignPreviewResponse {
  slots: SlotPreviewItem[];
  total: number;
  with_patient: number;
  without_patient: number;
}

export interface BulkReassignResult {
  affected_slots: number;
  log_id: string;
}

export async function previewReassignSlots(
  clinic_id: string,
  slot_date: string,
  dentist_from_id: string,
  time_from: string,
  time_to: string,
): Promise<BulkReassignPreviewResponse> {
  const params = new URLSearchParams({ clinic_id, slot_date, dentist_from_id, time_from, time_to });
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/schedule-slot/bulk-reassign/preview?${params}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Erro ao buscar prévia da substituição');
  }

  return response.json();
}

// --- Agenda Slot-Centric ---

export interface SlotAppointment {
  appointment_id: string;
  patient_id: string | null;
  patient_name: string;
  appointment_status: string;
  confirmed: boolean;
  duration_minutes: number;
  slot_order: number;
  dental_insurance_name: string;
  is_new_patient: boolean;
  observations: string;
}

export interface AgendaSlot {
  slot_id: string;
  slot_date: string;
  slot_time: string;
  consulting_room_id: string;
  consulting_room_name: string;
  dental_specialty_id: string | null;
  dental_specialty_name: string;
  dentist_id: string | null;
  dentist_name: string;
  block_type: string | null;
  appointments: SlotAppointment[];
}

export interface FitInAppointment {
  appointment_id: string;
  slot_date: string;
  slot_time: string;
  dentist_id: string | null;
  dentist_name: string;
  consulting_room_id: string | null;
  consulting_room_name: string;
  dental_specialty_id: string | null;
  dental_specialty_name: string;
  patient_id: string | null;
  patient_name: string;
  appointment_status: string;
  confirmed: boolean;
  duration_minutes: number;
  dental_insurance_name: string;
  is_new_patient: boolean;
  observations: string;
}

export interface AgendaResponse {
  slots: AgendaSlot[];
  fit_ins: FitInAppointment[];
}

export async function fetchSlotAgenda(
  clinic_id: string,
  start_date: string,
  end_date: string,
  dentist_id?: string,
): Promise<AgendaResponse> {
  const params = new URLSearchParams({ clinic_id, start_date, end_date });
  if (dentist_id) params.append('dentist_id', dentist_id);
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/schedule-slot/agenda?${params}`);
  if (!response.ok) throw new Error('Erro ao carregar agenda');
  return response.json();
}

// --- Blocked Slots (legado, mantido para compatibilidade) ---

export interface BlockedSlot {
  slot_id: string;
  slot_date: string;
  slot_time: string;
  dentist_id: string | null;
  block_type: 'BLOCKED' | 'BLOCKED_EXTERNAL';
}

export async function fetchBlockedSlots(
  clinic_id: string,
  date_from: string,
  date_to: string,
): Promise<BlockedSlot[]> {
  const params = new URLSearchParams({ clinic_id, date_from, date_to });
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/schedule-slot/blocked-slots?${params}`);
  if (!response.ok) return [];
  return response.json();
}

export async function setSlotBlockType(
  slot_id: string,
  block_type: 'BLOCKED' | 'BLOCKED_EXTERNAL' | null,
): Promise<void> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/schedule-slot/${slot_id}/block-type`, {
    method: 'PATCH',
    body: JSON.stringify({ block_type }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Erro ao alterar bloqueio do slot');
  }
}

export interface BulkBlockRequest {
  clinic_id: string;
  slot_date: string;
  dentist_from_id: string;
  time_from: string;
  time_to: string;
  block_type: 'BLOCKED' | 'BLOCKED_EXTERNAL';
  reason?: string;
}

export async function bulkBlockSlots(data: BulkBlockRequest): Promise<{ affected_slots: number }> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/schedule-slot/bulk-block`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Erro ao bloquear slots');
  }
  return response.json();
}

export async function bulkReassignSlots(data: BulkReassignRequest): Promise<BulkReassignResult> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/schedule-slot/bulk-reassign`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Erro ao realizar substituição de turno');
  }

  return response.json();
}
